import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Quote, ChevronLeft, ChevronRight, Star, ArrowUpRight, ArrowDown,
  Zap, Shield, Users, BarChart3 as BarChart, Globe, Palette, HeadphonesIcon, TrendingUp,
  Brain, Rocket, ShieldCheck, Linkedin, ExternalLink, Code2, Radio, Cloud, Compass,
  Server, Cpu, Layers, Lock, Sparkles, Terminal, CreditCard, ScanFace, Video, Briefcase, Network,
  X, Smartphone, Phone, AlertTriangle, FileText, CheckCircle2, ChevronDown, Award } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Reveal, CountUp, Magnetic } from "./motion";
import { BrowserMock, PhoneMock, FloatingStat, AbstractOrb, Spotlight } from "./Visuals";
import { openConsultationModal } from "./ConsultationModal";
import api from "../lib/api";

import {
  STATS, SERVICES, INDUSTRIES, SOLUTIONS, PROJECTS, PROCESS, TECHNOLOGIES, WHY,
  TESTIMONIALS, TEAM, BLOGS, FAQS, CLIENTS, SECTOR_CASE_STUDIES, SECTOR_LABELS,
  ALL_OFFERINGS,
} from "../data";

export const SectionHead = ({ eyebrow, title, sub, id, center = false, dark = false }) => (
  <div id={id} className={`max-w-3xl mb-16 scroll-mt-28 ${center ? "mx-auto text-center" : ""}`}>
    <Reveal><span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-sm font-semibold uppercase tracking-[0.22em] mb-5"><span className="w-8 h-px bg-[#FF5A1F]" />{eyebrow}</span></Reveal>
    <Reveal delay={0.1}><h2 className={`font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.05] text-balance ${dark ? "text-white" : "text-[#0B0B0B]"}`}>{title}</h2></Reveal>
    {sub && <Reveal delay={0.2}><p className={`mt-6 text-lg leading-relaxed ${dark ? "text-neutral-400" : "text-neutral-500"}`}>{sub}</p></Reveal>}
  </div>
);

/* Clients — glass marquee */
export const TrustedBy = () => {
  const [clientList, setClientList] = useState(CLIENTS);

  useEffect(() => {
    api.get("/clients")
      .then((res) => {
        if (res.data && res.data.length > 0) setClientList(res.data);
      })
      .catch(() => {});
  }, []);

  const strip = [...clientList, ...clientList];
  return (
    <section className="py-8 sm:py-14 mesh-light overflow-hidden border-y border-black/5">
      <p className="text-center text-xs uppercase tracking-[0.28em] text-neutral-400 mb-6 sm:mb-10 font-semibold">
        Trusted by ambitious teams worldwide
      </p>
      <div className="flex gap-4 sm:gap-6 w-max animate-marquee items-center">
        {strip.map((c, i) => {
          let logoUrl = c.logo;
          if (!logoUrl) {
            if (c.name.toLowerCase().includes("citydekho") || (c.domain && c.domain.includes("citydekho"))) {
              logoUrl = "/brand/citydekho-pin.png";
            } else if (c.name.toLowerCase().includes("ecopro") || (c.domain && c.domain.includes("ecopro"))) {
              logoUrl = "/brand/ecopro-logo.png";
            } else {
              logoUrl = `https://www.google.com/s2/favicons?domain=${c.domain}&sz=128`;
            }
          }
          const projectMatch = PROJECTS.find(
            (p) =>
              p.title.toLowerCase() === c.name.toLowerCase() ||
              (p.domain && c.domain && p.domain.toLowerCase() === c.domain.toLowerCase()) ||
              p.slug === c.name.toLowerCase().replace(/\s+/g, "-") ||
              (c.domain && p.slug === c.domain.replace(/\.[a-z]+$/, ""))
          );

          if (projectMatch) {
            return (
              <Link
                key={i}
                to={`/portfolio/${projectMatch.slug}`}
                className="glass-card rounded-2xl px-6 py-3.5 flex items-center gap-3 font-display font-semibold text-neutral-700 hover:text-[#FF5A1F] transition-all hover:scale-105 border border-black/5 hover:border-[#FF5A1F]/30 group"
                title={`Explore ${c.name} Case Study`}
              >
                <img
                  src={logoUrl}
                  alt={c.name}
                  className="w-6 h-6 rounded-md object-contain shrink-0"
                  onError={(e) => {
                    if (c.name.toLowerCase().includes("citydekho")) {
                      e.target.onerror = null;
                      e.target.src = "/brand/citydekho-pin.png";
                    } else if (c.name.toLowerCase().includes("ecopro")) {
                      e.target.onerror = null;
                      e.target.src = "/brand/ecopro-logo.png";
                    } else {
                      e.target.style.display = "none";
                    }
                  }}
                />
                <span className="whitespace-nowrap text-sm sm:text-base">{c.name}</span>
              </Link>
            );
          }

          return (
            <a
              key={i}
              href={`https://${c.domain}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="glass-card rounded-2xl px-6 py-3.5 flex items-center gap-3 font-display font-semibold text-neutral-700 hover:text-[#FF5A1F] transition-all hover:scale-105 border border-black/5 hover:border-[#FF5A1F]/30"
            >
              <img
                src={logoUrl}
                alt={c.name}
                className="w-6 h-6 rounded-md object-contain shrink-0"
                onError={(e) => {
                  if (c.name.toLowerCase().includes("citydekho")) {
                    e.target.onerror = null;
                    e.target.src = "/brand/citydekho-pin.png";
                  } else if (c.name.toLowerCase().includes("ecopro")) {
                    e.target.onerror = null;
                    e.target.src = "/brand/ecopro-logo.png";
                  } else {
                    e.target.style.display = "none";
                  }
                }}
              />
              <span className="whitespace-nowrap text-sm sm:text-base">{c.name}</span>
            </a>
          );
        })}
      </div>

    </section>
  );
};



/* Who we are — split with abstract AI visual */
export const AboutIntro = () => (
  <section className="py-10 sm:py-16 px-5 sm:px-8 mesh-light" id="about-intro">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
      <div>
        <span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] mb-3">
          <span className="w-6 h-px bg-[#FF5A1F]" />WHO WE ARE
        </span>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.1] text-[#0B0B0B] mb-4">
          Building, Scaling & Managing <span className="accent-underline">Intelligent Digital Systems</span>
        </h2>

        <Reveal delay={0.1}>
          <p className="text-base sm:text-lg text-neutral-600 leading-relaxed font-normal">
            We don't just build software: we engineer, deploy, secure, and continuously manage digital products that power modern businesses. Whether it's AI, enterprise platforms, cloud infrastructure, or ongoing application maintenance, Neuravolt provides end-to-end technology solutions from concept to long-term success.
          </p>
        </Reveal>

        {/* Core Pillars / Capabilities Tags */}
        <Reveal delay={0.2}>
          <div className="mt-5 flex flex-wrap gap-2">
            {["AI Solutions", "Cloud Infrastructure", "Managed Services", "24×7 Support"].map((pill) => (
              <span
                key={pill}
                className="px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide bg-[#FF5A1F]/10 text-[#FF5A1F] border border-[#FF5A1F]/20"
              >
                {pill}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-[#FF5A1F] font-semibold text-sm group">
            Learn our story <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </div>

      {/* Right Column: Abstract Orb with Enterprise Metrics Cards */}
      <Reveal delay={0.2}>
        <AbstractOrb />
      </Reveal>
    </div>
  </section>
);




/* Numbers — 5-column iOS glass grid with cursor 3D tilt */
const STAT_ICONS = [Rocket, Users, Globe, HeadphonesIcon, Zap];
const STAT_SUBTEXT = [
  "Shipped on schedule",
  "Across 12 countries",
  "Modern frameworks",
  "Round-the-clock SLA",
  "High availability"
];

function TiltStatCard({ children, testId, delay }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -12;
    const rotateY = ((x - centerX) / centerX) * 12;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.03, 1.03, 1.03)`;
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)";
  };

  return (
    <Reveal delay={delay}>
      <div
        ref={cardRef}
        data-testid={testId}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="ios-stat-card rounded-3xl p-6 sm:p-7 flex flex-col justify-between h-full cursor-default select-none transition-all duration-200 ease-out"
        style={{
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </Reveal>
  );
}

export const Numbers = () => (
  <section className="py-12 sm:py-20 px-5 sm:px-8 grad-warm relative overflow-hidden">
    <style>{`
      .ios-stat-card {
        background: rgba(255, 255, 255, 0.10);
        backdrop-filter: blur(24px) saturate(180%);
        -webkit-backdrop-filter: blur(24px) saturate(180%);
        border: 1px solid rgba(255, 255, 255, 0.20);
        box-shadow: 0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.22);
        border-top: 2px solid rgba(255, 90, 31, 0.75);
      }
      .ios-stat-card:hover {
        border-color: rgba(255, 90, 31, 0.9);
        box-shadow: 0 20px 40px -10px rgba(255, 90, 31, 0.3), inset 0 1px 0 rgba(255,255,255,0.35);
        background: rgba(255, 255, 255, 0.14);
      }
    `}</style>
    <div className="blob w-[500px] h-[500px] bg-[#FF5A1F] top-0 right-10 animate-aurora" style={{ opacity: 0.2 }} />
    <div className="blob w-[320px] h-[320px] bg-[#FF5A1F] bottom-0 left-20 animate-aurora" style={{ opacity: 0.14, animationDelay: "2.5s" }} />

    <div className="max-w-7xl mx-auto relative z-10">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
        {STATS.map((s, i) => {
          const Icon = STAT_ICONS[i] || Zap;
          const sub = STAT_SUBTEXT[i];
          const isProjects = i === 0;

          return (
            <TiltStatCard key={s.label} testId={`stat-${i}`} delay={i * 0.07}>
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#FF5A1F]/15 border border-[#FF5A1F]/30">
                    <Icon className="w-5 h-5 text-[#FF5A1F]" />
                  </div>
                  {isProjects && (
                    <span className="text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#FF5A1F]/15 text-[#FF5A1F] border border-[#FF5A1F]/30">
                      Verified
                    </span>
                  )}
                </div>

                <div className="font-display text-4xl sm:text-5xl font-black text-white leading-none tracking-tight drop-shadow-md">
                  <CountUp end={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                </div>

                <div className="mt-2.5 font-heading text-base font-semibold text-white/90">
                  {isProjects ? "Projects Delivered" : s.label}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-white/10 text-xs text-white/50 font-medium">
                {sub}
              </div>
            </TiltStatCard>
          );
        })}
      </div>
    </div>
  </section>
);




export const ServicesShowcase = ({ isPage = false }) => {
  const [filter, setFilter] = useState("all");

  const displayedItems = ALL_OFFERINGS.filter((item) => {
    if (filter === "services") return item.type === "service";
    if (filter === "products") return item.type === "product";
    return true;
  });

  return (
    <section className={`py-16 sm:py-24 px-5 sm:px-8 mesh-light ${isPage ? "pt-8 sm:pt-12" : ""}`} id="services">
      <div className="max-w-7xl mx-auto">
        {/* Main Section Header or Page Filter Header */}
        {!isPage ? (
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <SectionHead
              eyebrow="CAPABILITIES & PRODUCTS"
              title={<>Services & Products Engineered for <span className="text-gradient">Scale</span></>}
              sub="Full-cycle software engineering, cognitive AI, dedicated infrastructure, and sovereign enterprise platforms."
            />

            {/* Interactive Filter Pills */}
            <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-black/5 border border-black/5 self-start md:self-auto shrink-0 mb-8 md:mb-16">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "all"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                All ({ALL_OFFERINGS.length})
              </button>
              <button
                onClick={() => setFilter("services")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "services"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Services (6)
              </button>
              <button
                onClick={() => setFilter("products")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "products"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Products (2)
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10 pb-6 border-b border-black/5">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5A1F] animate-pulse" />
              <p className="text-sm font-semibold text-neutral-800">
                All 8 Unified Capabilities & Products
              </p>
            </div>
            <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-black/5 border border-black/5">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "all"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                All ({ALL_OFFERINGS.length})
              </button>
              <button
                onClick={() => setFilter("services")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "services"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Services (6)
              </button>
              <button
                onClick={() => setFilter("products")}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  filter === "products"
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/20"
                    : "text-neutral-600 hover:text-black hover:bg-black/5"
                }`}
              >
                Products (2)
              </button>
            </div>
          </div>
        )}

        {/* Responsive Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {displayedItems.map((u, i) => {
            const Icon = u.icon;
            const CardWrapper = u.isExternal ? "a" : Link;
            const wrapperProps = u.isExternal
              ? { href: u.link, target: "_blank", rel: "noopener noreferrer" }
              : { to: u.link };

            return (
              <Reveal key={u.id} delay={(i % 3) * 0.05}>
                <CardWrapper
                  {...wrapperProps}
                  className="group gborder glass-card rounded-2xl p-6 sm:p-7 h-full flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-[#FF5A1F]/40 block cursor-pointer"
                >
                  <div>
                    {/* Top Bar: Icon + Clean Brand Logo (No Outer Box) or Badge */}
                    <div className="flex items-center justify-between gap-3 mb-5 min-h-[44px] sm:min-h-[48px]">
                      <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 grid place-items-center text-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors duration-300 shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>

                      {u.brandLogo ? (
                        <div className="min-w-0 flex-1 flex items-center justify-end pl-2">
                          <img
                            src={u.brandLogo}
                            alt={u.brandAlt || u.title}
                            className="h-7 sm:h-8 md:h-9 w-auto max-w-[125px] sm:max-w-[145px] md:max-w-[165px] object-contain transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = "none";
                              const badge = e.target.closest("div").nextSibling;
                              if (badge) badge.style.display = "inline-flex";
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-black/5 text-neutral-500 uppercase tracking-wider shrink-0">
                          {u.badge}
                        </span>
                      )}
                    </div>

                    {/* Card Title */}
                    <h3 className="font-display text-xl font-bold text-[#0B0B0B] group-hover:text-[#FF5A1F] transition-colors mb-2">
                      {u.title}
                    </h3>

                    {/* Concise 1-sentence description */}
                    <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed mb-5">
                      {u.desc}
                    </p>
                  </div>

                  {/* Minimalist Bottom Bar: Tags + Action Link */}
                  <div className="pt-4 border-t border-black/5 flex items-center justify-between gap-2 mt-auto">
                    <div className="flex flex-wrap gap-1.5">
                      {u.tags.map((t) => (
                        <span key={t} className="text-[10px] font-medium px-2 py-0.5 rounded bg-black/5 text-neutral-600">
                          {t}
                        </span>
                      ))}
                    </div>

                    <div className="text-xs font-semibold text-[#FF5A1F] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 shrink-0">
                      <span>{u.actionLabel || (u.isExternal ? "Visit" : "Explore")}</span>
                      {u.isExternal ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                    </div>
                  </div>
                </CardWrapper>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const NorbitShowcase = () => null;


/* Services grid - unified offerings */
export const ServicesGrid = ({ heading = true }) => (
  <ServicesShowcase isPage={!heading} />
);



/* ─── Animated Industry Icons ─────────────────────────────────────────────── */
/* Each icon is a bespoke animated SVG — no CDN, no external deps. */

function IndIcon({ isHovered, children, size = 52 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 52 52" fill="none"
      xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {children}
    </svg>
  );
}

/* Healthcare — heartbeat draw */
const IcoHealthcare = ({ h }) => (
  <IndIcon>
    <motion.circle cx="26" cy="26" r="22" stroke="#FF5A1F" strokeWidth="1"
      initial={{ opacity: 0.08 }} animate={{ opacity: h ? 0.18 : 0.08 }} transition={{ duration: 0.4 }} />
    <motion.path
      d="M6 26 L12 26 L16 16 L21 36 L25 22 L29 26 L36 26 L40 20 L44 26 L46 26"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      fill="none"
      initial={{ pathLength: 0 }} animate={{ pathLength: h ? 1 : 0.35 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
    />
  </IndIcon>
);

/* Education — mortarboard with rising tassel */
const IcoEducation = ({ h }) => (
  <IndIcon>
    <motion.polygon points="26,10 46,20 26,30 6,20"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none"
      animate={{ rotateY: h ? 10 : 0 }} transition={{ duration: 0.5 }} />
    <motion.rect x="20" y="30" width="12" height="9" rx="2"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none"
      animate={{ y: h ? 28 : 30 }} transition={{ duration: 0.4, ease: "backOut" }} />
    <motion.line x1="46" y1="20" x2="46" y2="30"
      stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="2" strokeLinecap="round" />
    <motion.circle cx="46" cy="33" r="3" fill={h ? "#FF5A1F" : "#6b7280"}
      animate={{ scale: h ? [1, 1.4, 1] : 1 }}
      transition={{ duration: 0.6, repeat: h ? Infinity : 0, repeatDelay: 0.4 }} />
  </IndIcon>
);

/* Finance — spinning coin with $ */
const IcoFinance = ({ h }) => (
  <IndIcon>
    <motion.circle cx="26" cy="26" r="18" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2"
      fill="none" animate={{ scaleX: h ? [1, 0.15, 1] : 1 }}
      transition={{ duration: 1.1, repeat: h ? Infinity : 0, repeatDelay: 0.6, ease: "easeInOut" }} />
    <motion.text x="26" y="31" textAnchor="middle" fontSize="14" fontWeight="700"
      fill={h ? "#FF5A1F" : "#9ca3af"} style={{ userSelect: "none" }}>$</motion.text>
    <motion.path d="M10 38 L20 42 L42 38" stroke={h ? "#FF5A1F" : "#6b7280"}
      strokeWidth="1.5" strokeLinecap="round" fill="none"
      initial={{ pathLength: 0 }} animate={{ pathLength: h ? 1 : 0 }}
      transition={{ duration: 0.5 }} />
  </IndIcon>
);

/* Real Estate — building rising floor by floor */
/* Real Estate — clean house silhouette, draw + glow on hover */
const IcoRealEstate = ({ h }) => (
  <IndIcon>
    {/* House outline: roof + walls as one clean path */}
    <motion.path
      d="M8 24 L26 8 L44 24 L44 44 L8 44 Z"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" strokeLinejoin="round"
      fill={h ? "rgba(255,90,31,0.07)" : "none"}
      initial={{ pathLength: 0.4 }}
      animate={{ pathLength: h ? 1 : 0.4 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
    />
    {/* Chimney */}
    <motion.rect x="32" y="10" width="5" height="8" rx="1"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="1.5" fill="none"
      animate={{ opacity: h ? 1 : 0.5 }} transition={{ duration: 0.3 }} />
    {/* Left window */}
    <motion.rect x="12" y="28" width="8" height="7" rx="1.5"
      stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="1.5"
      fill={h ? "rgba(255,90,31,0.25)" : "transparent"}
      transition={{ duration: 0.35 }} />
    {/* Right window */}
    <motion.rect x="32" y="28" width="8" height="7" rx="1.5"
      stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="1.5"
      fill={h ? "rgba(255,90,31,0.25)" : "transparent"}
      transition={{ duration: 0.35, delay: 0.05 }} />
    {/* Door */}
    <motion.path d="M22 44 L22 36 Q26 33 30 36 L30 44"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" fill="none"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: h ? 1 : 0.6 }}
      transition={{ duration: 0.4, delay: 0.1 }} />
    {/* Door knob */}
    <motion.circle cx="29" cy="40" r="1.2" fill={h ? "#FF5A1F" : "#6b7280"}
      animate={{ scale: h ? 1.3 : 1 }} transition={{ duration: 0.25 }} />
  </IndIcon>
);


/* Retail — swinging shopping bag */
const IcoRetail = ({ h }) => (
  <IndIcon>
    <motion.path d="M16 20 L14 42 L38 42 L36 20 Z"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" strokeLinejoin="round"
      animate={{ rotate: h ? [-4, 4, -4] : 0, originX: "50%", originY: "top" }}
      transition={{ duration: 0.8, repeat: h ? Infinity : 0, ease: "easeInOut" }} />
    <motion.path d="M20 20 C20 14 32 14 32 20"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" strokeLinecap="round" />
    <motion.line x1="26" y1="28" x2="26" y2="34"
      stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round"
      animate={{ pathLength: h ? 1 : 0.5 }} />
    <motion.line x1="22" y1="31" x2="30" y2="31"
      stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round" />
  </IndIcon>
);

/* Manufacturing — rotating gear */
const IcoManufacturing = ({ h }) => (
  <IndIcon>
    <motion.g animate={{ rotate: h ? 360 : 0 }} transition={{ duration: 2, repeat: h ? Infinity : 0, ease: "linear" }}
      style={{ originX: "26px", originY: "26px" }}>
      <circle cx="26" cy="26" r="8" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, idx) => {
        const rad = (angle * Math.PI) / 180;
        const x1 = 26 + 10 * Math.cos(rad), y1 = 26 + 10 * Math.sin(rad);
        const x2 = 26 + 15 * Math.cos(rad), y2 = 26 + 15 * Math.sin(rad);
        return <line key={idx} x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="3" strokeLinecap="round" />;
      })}
    </motion.g>
    <circle cx="26" cy="26" r="4" fill={h ? "#FF5A1F" : "#6b7280"} />
  </IndIcon>
);

/* Logistics — truck sliding in */
const IcoLogistics = ({ h }) => (
  <IndIcon>
    <motion.g animate={{ x: h ? [0, -3, 0] : 0 }} transition={{ duration: 0.6, repeat: h ? Infinity : 0, repeatDelay: 0.4 }}>
      <rect x="6" y="20" width="24" height="16" rx="2" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" />
      <path d="M30 24 L42 24 L46 32 L46 36 L30 36 Z" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" strokeLinejoin="round" />
      <circle cx="14" cy="38" r="4" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" />
      <circle cx="38" cy="38" r="4" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" />
    </motion.g>
    <motion.path d="M4 38 L6 38" stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="2" strokeLinecap="round"
      animate={{ x: h ? [-4, 0] : 0, opacity: h ? 1 : 0.4 }} transition={{ duration: 0.4, repeat: h ? Infinity : 0 }} />
  </IndIcon>
);

/* Government — shield with pulse */
const IcoGovernment = ({ h }) => (
  <IndIcon>
    <motion.path d="M26 8 L42 14 L42 28 C42 38 26 46 26 46 C26 46 10 38 10 28 L10 14 Z"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" strokeLinejoin="round"
      animate={{ scale: h ? [1, 1.04, 1] : 1 }} transition={{ duration: 1, repeat: h ? Infinity : 0 }} />
    <motion.path d="M20 26 L24 30 L32 22" stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" fill="none"
      initial={{ pathLength: 0 }} animate={{ pathLength: h ? 1 : 0 }}
      transition={{ duration: 0.4, delay: 0.1 }} />
  </IndIcon>
);

/* Hospitality — steam rising from cup */
const IcoHospitality = ({ h }) => (
  <IndIcon>
    <rect x="14" y="28" width="24" height="14" rx="3" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill="none" />
    <path d="M38 31 C44 31 44 38 38 38" stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" fill="none" />
    <line x1="14" y1="45" x2="38" y2="45" stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="2" strokeLinecap="round" />
    {[20, 26, 32].map((x, i) => (
      <motion.path key={i} d={`M${x} 26 C${x - 2} 22 ${x + 2} 18 ${x} 14`}
        stroke={h ? "#FF5A1F" : "#6b7280"} strokeWidth="1.6" strokeLinecap="round" fill="none"
        animate={{ y: h ? [-3, 0] : 0, opacity: h ? [0, 1, 0] : 0.3 }}
        transition={{ duration: 1.2, delay: i * 0.2, repeat: h ? Infinity : 0 }} />
    ))}
  </IndIcon>
);

/* Agriculture — sprouting leaf */
const IcoAgriculture = ({ h }) => (
  <IndIcon>
    <motion.path d="M26 42 L26 20"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2.2" strokeLinecap="round" fill="none"
      animate={{ pathLength: h ? 1 : 0.6 }} transition={{ duration: 0.5 }} />
    <motion.path d="M26 20 C26 20 38 18 40 10 C40 10 28 8 26 20 Z"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill={h ? "rgba(255,90,31,0.15)" : "none"}
      initial={{ scale: 0, originX: "26px", originY: "20px" }}
      animate={{ scale: h ? 1 : 0.7 }} transition={{ duration: 0.45, ease: "backOut" }} />
    <motion.path d="M26 26 C26 26 14 22 12 12 C12 12 24 12 26 26 Z"
      stroke={h ? "#FF5A1F" : "#9ca3af"} strokeWidth="2" fill={h ? "rgba(255,90,31,0.15)" : "none"}
      animate={{ scale: h ? 1 : 0.6 }} transition={{ duration: 0.45, delay: 0.08, ease: "backOut" }} />
    <motion.path d="M18 44 Q26 46 34 44" stroke={h ? "#FF5A1F" : "#6b7280"}
      strokeWidth="1.8" strokeLinecap="round" fill="none"
      animate={{ pathLength: h ? 1 : 0 }} transition={{ duration: 0.4, delay: 0.15 }} />
  </IndIcon>
);

/* Arts & Culture: palette & brush with stroke */
const IcoArtsCulture = ({ h }) => (
  <IndIcon>
    <motion.path
      d="M26 12 C16 12 10 18 10 28 C10 38 18 42 24 42 C26 42 28 40 28 38 C28 36 26 34 26 32 C26 29 28 27 31 27 L36 27 C41 27 44 24 44 19 C44 14 36 12 26 12 Z"
      stroke={h ? "#FF5A1F" : "#9ca3af"}
      strokeWidth="2"
      fill={h ? "rgba(255,90,31,0.15)" : "none"}
      strokeLinejoin="round"
      animate={{ scale: h ? [1, 1.05, 1] : 1 }}
      transition={{ duration: 0.8, repeat: h ? Infinity : 0 }}
    />
    <circle cx="17" cy="22" r="2.2" fill={h ? "#FF5A1F" : "#6b7280"} />
    <circle cx="24" cy="18" r="2.2" fill={h ? "#FF5A1F" : "#6b7280"} />
    <circle cx="32" cy="19" r="2.2" fill={h ? "#FF5A1F" : "#6b7280"} />
    <circle cx="37" cy="24" r="2.2" fill={h ? "#FF5A1F" : "#6b7280"} />
  </IndIcon>
);

const IND_LIST = [
  { name: "Healthcare",    slug: "healthcare",    Ico: IcoHealthcare,    sectorKey: "Healthcare" },
  { name: "Education",     slug: "education",     Ico: IcoEducation,     sectorKey: "Education" },
  { name: "B2B Industry",  slug: "b2b-industry",  Ico: IcoFinance,       sectorKey: "B2B Industry", label: "B2B Industry" },
  { name: "Real Estate",   slug: "real-estate",   Ico: IcoRealEstate,    sectorKey: "Real Estate" },
  { name: "Retail",        slug: "retail",        Ico: IcoRetail,        sectorKey: "Retail" },
  { name: "Manufacturing", slug: "manufacturing", Ico: IcoManufacturing, sectorKey: "Manufacturing" },
  { name: "Logistics",     slug: "logistics",     Ico: IcoLogistics,     sectorKey: "Logistics" },
  { name: "Government",    slug: "government",    Ico: IcoGovernment,    sectorKey: "Government" },
  { name: "Hospitality",   slug: "hospitality",   Ico: IcoHospitality,   sectorKey: "Hospitality" },
  { name: "Agriculture",   slug: "agriculture",   Ico: IcoAgriculture,   sectorKey: "Agriculture" },
  { name: "Arts & Culture",slug: "arts-culture",  Ico: IcoArtsCulture,   sectorKey: "Arts & Culture", label: "Arts & Culture" },
];

function IndustryCard({ ind, i }) {
  const [hovered, setHovered] = useState(false);
  const { Ico } = ind;

  return (
    <Reveal delay={(i % 6) * 0.04}>
      <motion.div
        data-testid={`industry-${i}`}
        className="gborder glass-card-dark rounded-2xl p-4 sm:p-6 flex flex-col items-center justify-center gap-2.5 cursor-pointer h-full min-h-[110px] sm:min-h-[140px] select-none transition-all duration-300 relative overflow-hidden group-hover:border-[#FF5A1F]/60 group-hover:bg-white/[0.08]"
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.96 }}
        transition={{ duration: 0.2 }}
      >
        {/* icon glow ring */}
        <div className="relative flex items-center justify-center">
          <motion.div
            animate={{
              opacity: hovered ? 0.9 : 0.25,
              scale: hovered ? 1.15 : 0.85,
            }}
            transition={{ duration: 0.3 }}
            style={{
              position: "absolute",
              inset: -10,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,90,31,0.4) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Ico h={hovered} />
        </div>

        <motion.span
          animate={{ color: hovered ? "#FF5A1F" : "#e5e7eb" }}
          transition={{ duration: 0.2 }}
          className="text-xs sm:text-sm font-semibold text-center leading-snug group-hover:text-[#FF5A1F] transition-colors"
        >
          {ind.label || ind.name}
        </motion.span>
      </motion.div>
    </Reveal>
  );
}

export const IndustriesGrid = () => (
  <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-dark relative overflow-hidden" id="industries">
    <div className="max-w-7xl mx-auto relative z-10">
      <SectionHead
        dark
        eyebrow="WHO WE'VE WORKED FOR"
        title="Trusted Across Sectors"
        sub="Domain-aware engineering for regulated, high-stakes environments. Click any sector to explore verified case studies & live platforms in a dedicated page."
      />

      {/* Industry Sector Grid (Seamless SPA navigation to dedicated sector page) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
        {IND_LIST.map((ind, i) => (
          <Link
            key={ind.name}
            to={`/industries/${ind.slug}`}
            className="block h-full outline-none focus:ring-2 focus:ring-[#FF5A1F]/50 rounded-2xl group transition-transform duration-200"
            title={`Explore ${ind.label || ind.name} Case Studies`}
          >
            <IndustryCard ind={ind} i={i} />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

/* Solutions — premium glass */
export const SolutionsGrid = () => (
  <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="solutions">
    <div className="max-w-7xl mx-auto">
      <SectionHead
        eyebrow="What we build"
        title="Services that move the needle"
        sub="From idea to production, the most in-demand digital capabilities delivered end-to-end."
      />


      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SOLUTIONS.map((s, i) => {
          const Icon = s.icon;
          const isHot = s.tag === "Trending" || s.tag === "Most Popular" || s.tag === "Top Demand";
          return (
            <Reveal key={s.title} delay={(i % 3) * 0.07}>
              <Spotlight className="group relative gborder glass-card rounded-3xl p-8 h-full flex flex-col gap-4 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_24px_60px_-20px_rgba(255,90,31,0.22)]">

                {/* row: number + tag */}
                <div className="flex items-center justify-between">
                  <span className="font-display text-4xl font-black text-[#FF5A1F]/20 leading-none select-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                    isHot
                      ? "bg-[#FF5A1F]/12 text-[#FF5A1F]"
                      : "bg-black/5 text-neutral-400"
                  }`}>
                    {s.tag}
                  </span>
                </div>

                {/* title */}
                <div>
                  <h3 className="font-heading text-xl font-bold text-[#0B0B0B] leading-tight mb-2">{s.title}</h3>
                  <p className="text-neutral-500 text-sm leading-relaxed flex-1">{s.desc}</p>
                </div>


                {/* bottom cta hint */}
                <div className="flex items-center gap-1.5 text-[#FF5A1F] text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 -mb-1">
                  <span>Learn more</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </Spotlight>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);


/* Featured project — big device mockups + floating stats (Home) */
export const FeaturedProject = () => {
  const p = PROJECTS.find((x) => x.slug === "buildhealer") || PROJECTS[0];

  return (
    <section className="py-14 sm:py-28 px-5 sm:px-8 grad-warm relative overflow-hidden">
      <div className="blob w-[520px] h-[520px] bg-[#FF5A1F] top-10 -left-20 animate-aurora" style={{ opacity: 0.18 }} />
      <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <span className="text-[#FF5A1F] font-heading text-sm font-semibold uppercase tracking-[0.22em]">Featured product</span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mt-4 leading-tight">{p.title}</h2>
          <p className="mt-5 text-lg text-neutral-400 leading-relaxed">{p.overview}</p>
          <div className="flex flex-wrap gap-2 mt-6">{p.tech.map((t) => <span key={t} className="text-sm px-3.5 py-1.5 rounded-full glass-card-dark text-neutral-200">{t}</span>)}</div>
          <Link to={`/portfolio/${p.slug}`} className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors group">View case study <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <BrowserMock src={p.img} alt={p.title} domain={p.domain || "neuravolt.in"} className="aspect-[16/10]" />
            <PhoneMock src="https://images.unsplash.com/photo-1526498460520-4c246339dccb?w=400&q=80" alt="mobile" className="absolute -bottom-8 -right-4 w-28 sm:w-36 aspect-[9/19]" />
            <FloatingStat value="+38%" label="Delivery velocity" className="absolute -top-6 -left-4" delay={0.2} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* Portfolio grid — larger cards, browser-framed */
export const PortfolioGrid = ({ items, heading = true, isHome = false }) => {
  const [projectList, setProjectList] = useState(items || PROJECTS);

  useEffect(() => {
    if (!items) {
      api.get("/projects")
        .then((res) => {
          if (res.data && res.data.length > 0) setProjectList(res.data);
        })
        .catch(() => {});
    } else {
      setProjectList(items);
    }
  }, [items]);

  const displayItems = isHome ? projectList.slice(0, 6) : projectList;

  return (
    <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="portfolio">
      <div className="max-w-7xl mx-auto">
        {heading && (
          <SectionHead
            eyebrow={isHome ? "WHAT WE'VE BUILT" : "CLIENT IMPACT & TRANSFORMATION"}
            title={isHome ? "What We've Built" : "Enterprise Client Impact Stories"}
            sub={isHome ? "Explore products, mobile applications, and enterprise platforms engineered, scaled, and managed by Neuravolt." : "How we partner with industry leaders to engineer, deploy, and manage mission-critical digital systems."}
          />
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayItems.map((p, i) => {
            let logoUrl = p.logo;
            if (!logoUrl) {
              if (p.slug === "mycitydekho" || p.title.toLowerCase().includes("citydekho")) {
                logoUrl = "/brand/citydekho-pin.png";
              } else if (p.slug === "ecopro-builders" || p.title.toLowerCase().includes("ecopro")) {
                logoUrl = "/brand/ecopro-logo.png";
              } else if (p.domain) {
                logoUrl = `https://www.google.com/s2/favicons?domain=${p.domain}&sz=128`;
              }
            }
            return (
              <Reveal key={p.id || p.slug || i} delay={(i % 3) * 0.08}>
                <Spotlight className="gborder glass-card rounded-3xl p-5 transition-all duration-300 hover:-translate-y-2 flex flex-col h-full justify-between">
                  <div>
                    <Link to={`/portfolio/${p.slug}`} className="block relative overflow-hidden rounded-2xl group/img">
                      <BrowserMock src={p.img} alt={p.title} domain={p.domain || "neuravolt.in"} className="aspect-[16/10] group-hover/img:scale-[1.02] transition-transform duration-500" />
                      <span className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full bg-[#FF5A1F] text-white shadow-md">
                        {SECTOR_LABELS[p.sector] || (p.sector ? p.sector.replace(/-/g, " ") : p.cat)}
                      </span>
                      {p.domain && (
                        <span className="absolute bottom-3 left-3 text-[11px] font-mono font-medium px-2.5 py-1 rounded-lg bg-black/80 backdrop-blur-md text-white border border-white/20">
                          {p.domain}
                        </span>
                      )}
                    </Link>
                    <div className="px-2 pt-5 pb-1">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <Link to={`/portfolio/${p.slug}`} className="flex items-center gap-2.5 group/title">
                          {logoUrl && (
                            <img
                              src={logoUrl}
                              alt={p.title}
                              className="w-6 h-6 rounded-md object-contain shrink-0 border border-black/10"
                              onError={(e) => {
                                if (p.slug === "mycitydekho" || p.title.toLowerCase().includes("citydekho")) {
                                  e.target.onerror = null;
                                  e.target.src = "/brand/citydekho-pin.png";
                                } else if (p.slug === "ecopro-builders" || p.title.toLowerCase().includes("ecopro")) {
                                  e.target.onerror = null;
                                  e.target.src = "/brand/ecopro-logo.png";
                                } else {
                                  e.target.style.display = "none";
                                }
                              }}
                            />
                          )}
                          <h3 className="font-heading text-xl font-bold text-[#0B0B0B] group-hover/title:text-[#FF5A1F] transition-colors">
                            {p.title}
                          </h3>
                        </Link>
                      </div>

                      <div className="flex items-center gap-1.5 text-xs text-[#FF5A1F] font-medium mb-3 bg-[#FF5A1F]/5 px-2.5 py-1 rounded-lg w-fit border border-[#FF5A1F]/15">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        <span>{p.badge || "Designed & Maintained by NeuraVolt"}</span>
                      </div>

                      <p className="text-neutral-500 text-sm leading-relaxed mb-4 line-clamp-2">
                        {p.desc}
                      </p>
                    </div>
                  </div>

                  <div className="px-2 pt-3 border-t border-black/5 flex items-center justify-between mt-auto">
                    <div className="flex flex-wrap gap-1.5">
                      {(Array.isArray(p.tech) ? p.tech : []).slice(0, 2).map((t) => (
                        <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-black/5 text-neutral-600 font-medium">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2.5">
                      {p.domain && (
                        <a
                          href={p.live_url?.startsWith("http") ? p.live_url : `https://${p.domain.replace(/^https?:\/\//, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative z-20 inline-flex items-center gap-1 text-xs text-neutral-700 font-medium hover:text-[#FF5A1F] transition-colors bg-black/5 hover:bg-black/10 px-2.5 py-1 rounded-full cursor-pointer"
                          title={`Visit ${p.title} live product`}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <span>Live</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <Link
                        to={`/portfolio/${p.slug}`}
                        className="relative z-20 inline-flex items-center gap-1 text-[#FF5A1F] font-semibold text-xs sm:text-sm hover:underline cursor-pointer"
                      >
                        Case Study <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </Spotlight>
              </Reveal>
            );
          })}
        </div>

        {isHome && (
          <div className="mt-14 text-center">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#0B0B0B] text-white text-sm font-semibold hover:bg-[#FF5A1F] transition-colors shadow-lg hover:shadow-[#FF5A1F]/20 group"
            >
              <span>Explore All Projects & Work</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};



/* Case study — 3-card grid preview */
export const CaseStudiesPreview = () => {
  const [cases, setCases] = useState(PROJECTS);

  useEffect(() => {
    api.get("/projects")
      .then((res) => {
        if (res.data && res.data.length > 0) setCases(res.data);
      })
      .catch(() => {});
  }, []);

  const featuredSlugs = ["indusdeal", "foxrun", "pawsup"];
  const featuredCases = featuredSlugs
    .map((slug) => {
      const p = cases.find((x) => x.slug === slug) || PROJECTS.find((x) => x.slug === slug);
      if (!p) return null;
      const fallback = PROJECTS.find((x) => x.slug === p.slug) || {};
      const safeImg = (p.img && !p.img.includes("lh3.googleusercontent.com")) ? p.img : (fallback.img || (p.slug === "indusdeal" ? "/brand/indusdeal-live-2026.png" : ""));
      return {
        ...fallback,
        ...p,
        img: safeImg,
        challenge: p.challenge || fallback.challenge || "Opaque industrial pricing and lengthy offline procurement cycles.",
        solution: p.solution || fallback.solution || "Built automated RFQ workflows, catalog search, and bidding systems."
      };
    })
    .filter(Boolean);

  return (
    <section className="py-12 sm:py-24 px-5 sm:px-8 mesh-dark relative overflow-hidden" id="case-studies">
      <div className="max-w-7xl mx-auto relative z-10">
        <SectionHead
          dark
          eyebrow="REAL WORK, REAL STORIES"
          title="Featured Client Case Studies"
          sub="Real-world engineering challenges solved with AI, modern web, and mobile architectures."
        />

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {featuredCases.map((p, i) => (
            <Reveal key={p.slug || i} delay={i * 0.1}>
              <div className="glass-card-dark rounded-3xl p-6 flex flex-col justify-between h-full border border-white/10 hover:border-[#FF5A1F]/50 transition-all duration-300 group">
                <div>
                  <div className="relative overflow-hidden rounded-2xl mb-5 aspect-[16/10]">
                    <img
                      src={p.img || (p.slug === "indusdeal" ? "/brand/indusdeal-live-2026.png" : "")}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        if (p.slug === "indusdeal") {
                          e.target.src = "/brand/indusdeal-live-2026.png";
                        } else if (p.slug === "mycitydekho") {
                          e.target.src = "/brand/mycitydekho-preview.png";
                        } else if (p.slug === "ecopro-builders") {
                          e.target.src = "/brand/ecopro-preview.png";
                        }
                      }}
                    />
                    <span className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full bg-[#FF5A1F] text-white shadow-md">
                      {SECTOR_LABELS[p.sector] || (p.sector ? p.sector.replace(/-/g, " ") : p.cat)}
                    </span>
                  </div>

                  <h3 className="font-heading text-2xl font-bold text-white mb-2">{p.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed mb-4 line-clamp-2">{p.desc}</p>

                  <div className="space-y-2.5 pt-4 border-t border-white/10 text-xs">
                    <div>
                      <span className="text-[#FF5A1F] font-bold uppercase tracking-wider block mb-0.5">Challenge</span>
                      <p className="text-neutral-300 line-clamp-2">{p.challenge}</p>
                    </div>
                    <div>
                      <span className="text-emerald-400 font-bold uppercase tracking-wider block mb-0.5">Solution</span>
                      <p className="text-neutral-300 line-clamp-2">{p.solution}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {(p.tech || []).slice(0, 2).map((t) => (
                      <span key={t} className="text-[11px] px-2.5 py-0.5 rounded-full bg-white/10 text-neutral-300 font-mono">
                        {t}
                      </span>
                    ))}
                  </div>
                  <Link to={`/portfolio/${p.slug}`} className="inline-flex items-center gap-1 text-[#FF5A1F] text-xs font-bold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                    View Case Study <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};


/* Process — glass on light */
export const ProcessSteps = () => (
  <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="process">
    <div className="max-w-7xl mx-auto">
      <SectionHead eyebrow="HOW WE OPERATE" title="A Process Built for Momentum" sub="Eight deliberate stages from first call to long-term lifecycle partnership." />


      {/* Desktop — horizontal stepper */}
      <div className="hidden lg:block relative mt-16">
        {/* connector line */}
        <div className="absolute top-[28px] left-[6%] right-[6%] h-[2px] bg-neutral-200 rounded-full" />
        <motion.div className="absolute top-[28px] left-[6%] h-[2px] bg-[#FF5A1F] rounded-full"
          style={{ boxShadow: "0 0 10px rgba(255,90,31,0.5)" }}
          initial={{ width: "0%" }}
          whileInView={{ width: "88%" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.4, ease: "easeInOut", delay: 0.3 }}
        />
        <div className="grid grid-cols-8 gap-2">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.09}>
              <div data-testid={`process-${i}`} className="group flex flex-col items-center text-center">
                {/* circle step */}
                <div className="relative w-14 h-14 rounded-full bg-white border-2 border-neutral-200 grid place-items-center mb-5
                  group-hover:border-[#FF5A1F] group-hover:bg-[#FF5A1F] transition-all duration-300 z-10 font-mono font-bold text-sm text-[#0B0B0B] group-hover:text-white shadow-sm">
                  {p.n}
                </div>
                <h3 className="font-heading text-sm font-bold text-[#0B0B0B] mb-1 leading-tight">{p.title}</h3>
                <p className="text-neutral-400 text-xs leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Mobile — compact card grid */}
      <div className="lg:hidden grid sm:grid-cols-2 gap-4 mt-10">
        {PROCESS.map((p, i) => (
          <Reveal key={p.n} delay={(i % 2) * 0.07}>
            <Spotlight className="gborder glass-card rounded-2xl p-5 h-full flex gap-4 items-start" data-testid={`process-mobile-${i}`}>
              <span className="w-9 h-9 rounded-full bg-[#FF5A1F] text-white font-mono font-bold text-xs grid place-items-center shrink-0">{p.n}</span>
              <div>
                <h3 className="font-heading text-sm font-bold text-[#0B0B0B] mb-0.5">{p.title}</h3>
                <p className="text-neutral-500 text-xs leading-relaxed">{p.desc}</p>
              </div>
            </Spotlight>
          </Reveal>
        ))}
      </div>

    </div>
  </section>
);

/* Technologies — glass marquee on dark with official vector logos */
export const TechMarquee = () => {
  const strip = [...TECHNOLOGIES, ...TECHNOLOGIES];
  return (
    <section className="py-12 sm:py-24 mesh-dark relative overflow-hidden" id="technologies">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-12">
        <SectionHead dark eyebrow="WHAT FUELS OUR SERVICES" title="Production-Grade Tech Stack" sub="We pick the right tool for the job and master it with production excellence." />
      </div>

      <div className="overflow-hidden py-2">
        <div className="flex gap-4 w-max marquee-slow">
          {strip.map((t, i) => {
            const item = typeof t === "string" ? { name: t, logo: "" } : t;
            return (
              <div key={i} className="glass-card-dark rounded-2xl px-6 py-4 flex items-center gap-3 font-heading text-base font-semibold text-neutral-200 whitespace-nowrap border border-white/10 hover:border-[#FF5A1F]/50 transition-all group">
                {item.logo && (
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="w-6 h-6 object-contain shrink-0 group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
                <span>{item.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* Why choose — unique icon per point */
const WHY_ICONS = [Brain, Zap, TrendingUp, BarChart, Globe, HeadphonesIcon, Shield, Palette];
const WHY_COLORS = ["#6366f1","#FF5A1F","#10b981","#f59e0b","#3b82f6","#ec4899","#8b5cf6","#14b8a6"];

export const WhyChoose = () => (
  <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="why">
    <div className="max-w-7xl mx-auto">
      <SectionHead eyebrow="WHY CHOOSE US" title="Why Choose Neuravolt" sub="Reasons ambitious teams choose us to build what matters." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={(i % 4) * 0.07}>
            <Spotlight className="group gborder glass-card rounded-2xl sm:rounded-3xl p-4 sm:p-7 h-full transition-all duration-300 hover:-translate-y-1.5" data-testid={`why-${i}`}>
              <span className="text-[10px] sm:text-xs font-mono font-bold text-[#FF5A1F] px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full bg-[#FF5A1F]/10 inline-block mb-3 sm:mb-4">
                {`0${i + 1}`}
              </span>
              <h3 className="font-heading text-sm sm:text-lg font-semibold mb-1.5 sm:mb-2 text-[#0B0B0B]">{w.title}</h3>
              <p className="text-neutral-500 text-xs sm:text-sm leading-relaxed">{w.desc}</p>
              <div className="mt-3 sm:mt-4 h-0.5 rounded-full w-8 transition-all duration-500 group-hover:w-full bg-gradient-to-r from-[#FF5A1F] to-transparent" />
            </Spotlight>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);


/* Testimonials — sleek compact review slider */
export const Testimonials = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIdx((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const t = TESTIMONIALS[idx];

  const projectMatch = PROJECTS.find(
    (p) =>
      p.title.toLowerCase() === (t.company || "").toLowerCase() ||
      p.slug === (t.company || "").toLowerCase().replace(/\s+/g, "-") ||
      (p.domain && (t.company || "").toLowerCase().includes(p.domain.replace(/\.[a-z]+$/, "")))
  );
  const clientMatch = CLIENTS.find(
    (c) => c.name.toLowerCase() === (t.company || "").toLowerCase()
  );
  let companyLogo = projectMatch?.logo || clientMatch?.logo;
  if (!companyLogo) {
    if ((t.company || "").toLowerCase().includes("citydekho")) companyLogo = "/brand/citydekho-pin.png";
    else if ((t.company || "").toLowerCase().includes("ecopro")) companyLogo = "/brand/ecopro-logo.png";
    else if (projectMatch?.domain) companyLogo = `https://www.google.com/s2/favicons?domain=${projectMatch.domain}&sz=128`;
    else if (clientMatch?.domain) companyLogo = `https://www.google.com/s2/favicons?domain=${clientMatch.domain}&sz=128`;
  }

  return (
    <section className="py-12 sm:py-20 mesh-dark relative overflow-hidden" id="testimonials">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <SectionHead dark center eyebrow="WHAT DO OUR CLIENTS SAY?" title="What Our Clients Say" sub="Live reviews from client teams across B2B, mobile, e-commerce, and enterprise." />

        <div className="glass-card-dark rounded-2xl sm:rounded-3xl p-6 sm:p-9 border border-white/10 shadow-2xl relative">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-1">
              {Array.from({ length: t.rating }).map((_, i) => (
                <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF5A1F]" fill="currentColor" />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {companyLogo && (
                <img
                  src={companyLogo}
                  alt={t.company}
                  className="w-5 h-5 rounded object-contain bg-white/10 p-0.5 border border-white/10 shrink-0"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              <span className="text-xs font-mono px-3 py-1 rounded-full bg-[#FF5A1F]/15 text-[#FF5A1F] border border-[#FF5A1F]/30 font-semibold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
                {t.company || "Client"}
              </span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              data-testid="testimonial"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="font-display text-base sm:text-xl font-medium leading-relaxed text-white"
            >
              “{t.quote}”
            </motion.blockquote>
          </AnimatePresence>

          <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF5A1F]/20 border border-[#FF5A1F]/40 font-bold text-white text-sm grid place-items-center uppercase shrink-0">
                {t.name.charAt(0)}
              </div>
              <div>
                <div className="font-heading font-semibold text-sm sm:text-base text-white">{t.name}</div>
                <div className="text-xs sm:text-sm text-neutral-400">{t.role}</div>
                {projectMatch && (
                  <Link
                    to={`/portfolio/${projectMatch.slug}`}
                    className="text-[11px] text-[#FF5A1F] hover:underline font-semibold inline-flex items-center gap-1 mt-1 group"
                  >
                    <span>Explore {projectMatch.title} Case Study</span>
                    <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                data-testid="testi-prev"
                onClick={() => setIdx((idx - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                className="w-9 h-9 rounded-full glass-card-dark grid place-items-center text-white hover:text-[#FF5A1F] hover:border-[#FF5A1F] transition-colors"
                aria-label="Previous review"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="testi-next"
                onClick={() => setIdx((idx + 1) % TESTIMONIALS.length)}
                className="w-9 h-9 rounded-full glass-card-dark grid place-items-center text-white hover:text-[#FF5A1F] hover:border-[#FF5A1F] transition-colors"
                aria-label="Next review"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* Leadership — premium founder glass profiles */
export const Leadership = () => {
  const [members, setMembers] = useState(TEAM);

  useEffect(() => {
    api.get("/team")
      .then((res) => {
        if (res.data && res.data.length > 0) setMembers(res.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="team">
      <div className="max-w-7xl mx-auto">
        <SectionHead eyebrow="Leadership" title="The people behind Neuravolt" sub="Founders and a core engineering team obsessed with craft." />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {members.map((m, i) => (
            <Reveal key={m.id || i} delay={(i % 4) * 0.07}>
              <div className="group relative editable">
                <Spotlight className="gborder glass-card rounded-2xl sm:rounded-3xl p-4 sm:p-7 text-center overflow-hidden flex flex-col items-center justify-center h-full">
                  <div className="blob w-32 h-32 bg-[#FF5A1F] -top-6 left-1/2 -translate-x-1/2 -z-10" style={{ opacity: 0.12 }} />
                  
                  {m.img ? (
                    <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl mx-auto mb-3 sm:mb-4 overflow-hidden border-2 border-[#FF5A1F]/30 shadow-lg shadow-[#FF5A1F]/10 relative bg-gradient-to-br from-[#FF5A1F] to-[#7a2b00] grid place-items-center">
                      <img
                        src={m.img}
                        alt={m.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          if (m.fallbackImg && e.target.src !== m.fallbackImg) {
                            e.target.src = m.fallbackImg;
                          } else {
                            e.target.style.display = "none";
                          }
                        }}
                      />
                      <span className="absolute inset-0 grid place-items-center text-white font-display text-lg sm:text-xl font-bold -z-10">
                        {m.initials || (m.name ? m.name.substring(0, 2).toUpperCase() : "NV")}
                      </span>
                    </div>
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl mx-auto mb-3 sm:mb-4 grid place-items-center bg-gradient-to-br from-[#FF5A1F] to-[#7a2b00] text-white font-display text-lg sm:text-xl font-bold" style={{ boxShadow: "0 12px 30px -10px rgba(255,90,31,0.6)" }}>
                      {m.initials || (m.name ? m.name.substring(0, 2).toUpperCase() : "NV")}
                    </div>
                  )}

                  <h3 className="font-heading text-sm sm:text-base font-semibold text-[#0B0B0B]">{m.name}</h3>
                  <p className="text-xs sm:text-sm text-[#FF5A1F] font-medium mt-0.5">{m.role}</p>
                  {m.bio && <p className="text-[11px] sm:text-xs text-neutral-500 mt-1.5 sm:mt-2 line-clamp-2">{m.bio}</p>}
                  {m.linkedin && (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0A66C2] bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 px-3 py-1 rounded-full font-medium transition-colors"
                      title={`Verify ${m.name} on LinkedIn`}
                    >
                      <Linkedin className="w-3.5 h-3.5" />
                      <span>Verified Profile</span>
                    </a>
                  )}
                </Spotlight>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* Blogs — editorial magazine layout (1 large + list) */
export const BlogsPreview = () => {
  const [lead, ...rest] = BLOGS.slice(0, 4);
  return (
    <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="blog">
      <div className="max-w-7xl mx-auto">
        <SectionHead eyebrow="Insights" title="From the Neuravolt journal" sub="Ideas on AI, engineering and digital transformation." />
        <div className="grid lg:grid-cols-2 gap-8">
          <Reveal>
            <Link to={`/blog/${lead.slug}`} className="group block gborder glass-card rounded-3xl overflow-hidden h-full">
              <div className="h-72 overflow-hidden"><img src={lead.img} alt={lead.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-8">
                <div className="flex items-center gap-2 text-xs text-neutral-400 mb-3"><span className="text-[#FF5A1F] font-semibold">{lead.category}</span> · {lead.read}</div>
                <h3 className="font-display text-2xl font-bold text-[#0B0B0B] leading-snug mb-2">{lead.title}</h3>
                <p className="text-neutral-500">{lead.excerpt}</p>
              </div>
            </Link>
          </Reveal>
          <div className="space-y-5">
            {rest.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.08}>
                <Link to={`/blog/${b.slug}`} className="group flex flex-col sm:flex-row gap-4 sm:gap-5 gborder glass-card rounded-2xl p-4 items-start sm:items-center">
                  <div className="w-full sm:w-28 h-40 sm:h-24 rounded-xl overflow-hidden shrink-0"><img src={b.img} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div>
                    <div className="text-xs text-[#FF5A1F] font-semibold mb-1">{b.category} · {b.read}</div>
                    <h3 className="font-heading font-semibold text-[#0B0B0B] leading-snug">{b.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-12 text-center"><Link to="/blog" className="inline-flex items-center gap-2 text-[#FF5A1F] font-semibold group">Read all articles <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Link></div>
      </div>
    </section>
  );
};

/* FAQ — glass accordion */
export const FAQ = () => (
  <section className="py-14 sm:py-28 px-5 sm:px-8 mesh-light" id="faq">
    <div className="max-w-3xl mx-auto">
      <SectionHead eyebrow="FAQ" title="Questions, answered" center />
      <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="glass-card rounded-2xl mb-3 px-6 border-none">
            <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-heading font-semibold hover:no-underline hover:text-[#FF5A1F] text-[#0B0B0B]">{f.q}</AccordionTrigger>
            <AccordionContent className="text-neutral-500 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* CTA — cinematic */
export const CTA = () => (
  <section className="py-12 sm:py-24 px-5 sm:px-8 mesh-light">
    <div className="max-w-6xl mx-auto mesh-dark rounded-[2.5rem] px-8 sm:px-16 py-24 text-center relative overflow-hidden border border-white/10">
      <div className="blob w-[400px] h-[400px] bg-[#FF5A1F] top-0 left-1/3 animate-aurora" style={{ opacity: 0.35 }} />
      <div className="relative z-10">
        <Reveal><h2 className="font-display text-4xl sm:text-6xl font-bold text-white leading-[1.05] text-balance">Let's build your <span className="text-gradient">breakthrough</span></h2></Reveal>
        <Reveal delay={0.15}><p className="mt-6 text-lg text-neutral-400 max-w-xl mx-auto">Book a free consultation and get a plan to move your product forward.</p></Reveal>
        <Reveal delay={0.3}>
          <Magnetic className="mt-10 inline-block">
            <button onClick={() => openConsultationModal()} data-testid="cta-btn" className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors" style={{ boxShadow: "0 16px 40px -12px rgba(255,90,31,0.6)" }}>
              Book Free Consultation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </Magnetic>
        </Reveal>

      </div>
    </div>
  </section>
);
